/**
 * Générateur — Connaître la formule de l'aire d'un carré ou d'un rectangle
 * Attendu ID : 645 | Niveau : 6ème | Domaine : Grandeurs et mesures
 *
 * Types générés : qcm_unique, relier, texte_trou_saisie
 * Labels de référence :
 *   - Compléter la verbalisation de la formule d'aire
 * Variantes estimées : 50+
 */

import {
    randomChoice, randomChoiceMultiple,
    makeQcm, makeTexteTrou, makeRelier,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 645;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Labels ───
const L_FORMULE = 'Compléter la verbalisation de la formule d\'aire';

// ─── Pools ───
// Pools élargis : les distracteurs sont TIRÉS (3 parmi 6) et les phrases à
// trous varient par formulation ET par position des trous — chaque position
// teste une autre partie de la formule
const FORMULES = [
    {
        figure: 'carré',
        bonneFormule: 'Aire = c × c',
        distracteursPool: [
            'Aire = 4 × c', 'Aire = 2 × c', 'Aire = c + c',
            'Aire = c + c + c + c', 'Aire = 2 × (c + c)', 'Aire = c × c × c',
        ],
        verbPhrases: [
            'L\'aire d\'un carré est égale au {{produit}} de son {{côté}} par son côté.\nOn écrit : Aire = {{c}} × c.',
            'Pour calculer l\'aire d\'un carré, on multiplie le {{côté}} par le {{côté}}.\nOn écrit : Aire = c × {{c}}.',
            'L\'aire d\'un carré de côté c s\'écrit : Aire = {{c}} × {{c}}.\nLe périmètre, lui, vaut {{4}} × c.',
            'Dans la formule Aire = c × c, la lettre c désigne le {{côté}} du carré.\nSi le côté double, l\'aire est multipliée par {{4}}.',
        ],
        verbTexte: '<p>Complète la définition de l\'aire d\'un carré.</p>',
    },
    {
        figure: 'rectangle',
        bonneFormule: 'Aire = L × l',
        distracteursPool: [
            'Aire = 2 × (L + l)', 'Aire = L + l', 'Aire = 2 × L × l',
            'Aire = L + L + l + l', 'Aire = 2 × L + l', 'Aire = (L + l) × 2',
        ],
        verbPhrases: [
            'L\'aire d\'un rectangle est égale au {{produit}} de sa longueur par sa {{largeur}}.\nOn écrit : Aire = L × {{l}}.',
            // « la longueur » en CLAIR (revue 2026-07-31, signalement utilisateur) :
            // avec deux trous, « on multiplie la largeur par la longueur » était
            // VRAI (commutativité verbale) et compté faux — le correcteur est
            // positionnel. Même règle que les produits symboliques du skill.
            'Pour calculer l\'aire d\'un rectangle, on multiplie la longueur par la {{largeur}}.\nOn écrit : Aire = {{L}} × l.',
            'Dans la formule Aire = L × l, la lettre L désigne la {{longueur}} et la lettre l la {{largeur}}.',
            // ⚠️ jamais {{L}} et {{l}} dans la même phrase : le scoring est
            // insensible à la casse → l'échange serait accepté (indécidable)
            // Une égalité par ligne (jamais « ; » entre deux égalités à trous :
            // l'impression coupait la ligne au milieu, convention 2026-09-02).
            'L\'aire d\'un rectangle s\'écrit Aire = L × {{l}}.\nSon périmètre s\'écrit P = {{2}} × (L + l).',
        ],
        verbTexte: '<p>Complète la définition de l\'aire d\'un rectangle.</p>',
    },
];

// ─── Générateurs ───

const genFormuleQcm = () => {
    const f = randomChoice(FORMULES);
    const choices = [f.bonneFormule, ...randomChoiceMultiple(f.distracteursPool, 3)];
    const texte = randomChoice([
        `<p>Quelle est la formule de l'aire d'un ${f.figure} ?</p>`,
        `<p>L'aire d'un ${f.figure} se calcule avec la formule :</p>`,
        `<p>Parmi ces formules, laquelle donne l'aire d'un ${f.figure} ?</p>`,
        `<p>Coche la formule correcte de l'aire d'un ${f.figure}.</p>`,
    ]);
    const payload = makeQcm(choices, choices[0], texte, opts(L_FORMULE));
    payload._meta.generatorKey = 'formule_aire';
    return payload;
};

const genFormuleRelier = () => {
    // Pool de paires (cibles toutes distinctes, libellés sources variés) :
    // on en tire 4 → C(6,4) combinaisons × variantes de libellés
    const POOL_PAIRES = [
        { sources: ['Aire d\'un carré de côté c', 'Aire du carré (côté c)'], target: 'c × c' },
        { sources: ['Aire d\'un rectangle (L, l)', 'Aire du rectangle de longueur L et largeur l'], target: 'L × l' },
        { sources: ['Périmètre d\'un carré de côté c', 'Tour complet d\'un carré de côté c'], target: '4 × c' },
        { sources: ['Périmètre d\'un rectangle (L, l)', 'Tour complet d\'un rectangle (L, l)'], target: '2 × (L + l)' },
        { sources: ['Demi-périmètre d\'un rectangle (L, l)', 'Moitié du périmètre d\'un rectangle (L, l)'], target: 'L + l' },
        { sources: ['Aire d\'un carré de côté 2 cm', 'Aire du carré de 2 cm de côté'], target: '4 cm²' },
    ];
    // Garde d'exclusion (revue 2026-07-31) : « Aire d'un carré de côté c »
    // (→ c × c) et « Aire d'un carré de côté 2 cm » (→ 4 cm²) ne sortent
    // jamais ensemble — relier le carré de 2 cm à « c × c » serait
    // mathématiquement défendable (c'est SA formule) mais compté faux.
    // La paire calculée est CONSERVÉE (décision utilisateur) : l'élève doit
    // distinguer « je peux calculer » de « je généralise par une formule » —
    // la consigne assume les deux (« sa formule ou sa valeur »).
    let tirage;
    do {
        tirage = randomChoiceMultiple(POOL_PAIRES, 4);
    } while (tirage.some(p => p.target === 'c × c') && tirage.some(p => p.target === '4 cm²'));
    const pairs = tirage.map(p => ({
        source: randomChoice(p.sources),
        target: p.target,
    }));
    const texte = '<p>Relie chaque grandeur à sa formule ou à sa valeur.</p>';
    const payload = makeRelier(pairs, texte, opts(L_FORMULE));
    payload._meta.generatorKey = 'formule_aire';
    return payload;
};

const genFormuleTrou = () => {
    const f = randomChoice(FORMULES);
    const phrase = randomChoice(f.verbPhrases);
    const payload = makeTexteTrou(phrase, f.verbTexte, { ...opts(L_FORMULE), mode: 'saisie', strict: false });
    payload._meta.generatorKey = 'formule_aire';
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    formule_aire_qcm_unique:         genFormuleQcm,
    formule_aire_relier:             genFormuleRelier,
    formule_aire_texte_trou_saisie:  genFormuleTrou,
};

// ─── Catégories ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'formule_aire', label: L_FORMULE, formatsList: buildFormats('formule_aire') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
